import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Public template workspaces for the sidebar (count aggregates only).
 * Also loads the current user's imported original_workspace_id set.
 */
const usePublicWorkspaces = (userId) => {
  const [publicWorkspaces, setPublicWorkspaces] = useState([]);
  const [importedOriginalIds, setImportedOriginalIds] = useState(() => new Set());

  const refreshImported = useCallback(async () => {
    if (!userId) {
      setImportedOriginalIds(new Set());
      return;
    }

    try {
      const { data, error } = await supabase
        .from('workspace_imports')
        .select('original_workspace_id')
        .eq('user_id', userId);

      if (error) throw error;

      setImportedOriginalIds(
        new Set(
          (data || [])
            .map((row) => row.original_workspace_id)
            .filter(Boolean)
        )
      );
    } catch (error) {
      console.error('Error refreshing workspace imports:', error);
    }
  }, [userId]);

  useEffect(() => {
    let cancelled = false;

    const fetchPublic = async () => {
      if (!userId) {
        setPublicWorkspaces([]);
        setImportedOriginalIds(new Set());
        return;
      }

      try {
        const [templatesResult, importsResult] = await Promise.all([
          supabase
            .from('workspaces')
            .select(`
              id,
              name,
              category,
              download_count,
              baskets:baskets(count),
              topics:topics(count)
            `)
            .eq('is_public', true)
            .eq('is_template', true)
            .order('download_count', { ascending: false })
            .limit(6),
          supabase
            .from('workspace_imports')
            .select('original_workspace_id')
            .eq('user_id', userId),
        ]);

        if (templatesResult.error) throw templatesResult.error;
        if (importsResult.error) throw importsResult.error;

        if (cancelled) return;

        const formatted = (templatesResult.data || []).map((workspace) => ({
          ...workspace,
          basketCount: workspace.baskets?.[0]?.count ?? 0,
          topicCount: workspace.topics?.[0]?.count ?? 0,
          downloadCount: workspace.download_count || 0,
        }));

        setPublicWorkspaces(formatted);
        setImportedOriginalIds(
          new Set(
            (importsResult.data || [])
              .map((row) => row.original_workspace_id)
              .filter(Boolean)
          )
        );
      } catch (error) {
        console.error('Error fetching public workspaces for sidebar:', error);
        if (!cancelled) {
          setPublicWorkspaces([]);
          setImportedOriginalIds(new Set());
        }
      }
    };

    fetchPublic();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const markImported = (originalWorkspaceId) => {
    setImportedOriginalIds((prev) => {
      const next = new Set(prev);
      next.add(originalWorkspaceId);
      return next;
    });
  };

  return {
    publicWorkspaces,
    importedOriginalIds,
    markImported,
    refreshImported,
  };
};

export default usePublicWorkspaces;
